import { useContext, useEffect, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";

import { GlobalContext } from "@/contexts/GlobalContext";

interface Product {
    brand?: string;
    title?: string;
    image?: string;
    ref_sku?: string;
};

interface Props {
    title: string;
    prod?: Product | null;
    rs?: boolean;
    bc?: string;
    genartnr?: string;
    genartname?: string;
    price?: string | number;
    stock?: string;
    make?: string;
    model?: string;
    ktype?: string;
    desc?: string | undefined;
    keywords?: string | undefined;
};

interface VhProps {
    make?: string;
    model?: string;
};

const SiteHead = ({
    title, 
    prod, 
    price, 
    stock, 
    genartnr, 
    genartname, 
    rs = false, 
    bc, 
    make, 
    model, 
    ktype, 
    desc, 
    keywords 
}: Props) => {
    const { globalState: { cookies } } = useContext(GlobalContext);
    const [vhLinks, setVhLinks] = useState<VhProps | null>(null);

    const router = useRouter();

    useEffect(() => {
        if (make && model) {
            setVhLinks({make: make!.toLowerCase().replace(/\s+|,\s/g, "-"), model: model!.toLowerCase().replace(/\s+|,\s/g, "-")});
        }
    }, [make, model]);

    const meta = [
        {
            name: `title`,
            content: `${title ? title + " | " + process.env.NEXT_PUBLIC_WEB_TITLE : process.env.NEXT_PUBLIC_WEB_TITLE}`
        },
        {
            name: `keywords`,
            content: router.pathname === "/" ? "car, parts, shop, online, doctor, dr, uk, oem, quality, delivery, auto, vehicle, europe, england, wales, scotland, ireland" : keywords
        },
        {
            name: `description`,
            content: router.pathname === "/" ? process.env.NEXT_PUBLIC_DESCRIPTION : desc
        },
        {
            property: `og:title`,
            content: `${title ? title + " | " + process.env.NEXT_PUBLIC_WEB_TITLE : process.env.NEXT_PUBLIC_WEB_TITLE}`
        },
        {
            property: `og:description`,
            content: router.pathname === "/" ? process.env.NEXT_PUBLIC_DESCRIPTION : desc
        },
        {
            property: `og:type`,
            content: `website`
        },
        {
            name: `twitter:card`,
            content: `summary`
        },
        // {
        //     name: `twitter:creator`,
        //     content: ``,
        // },
        {
            name: `twitter:title`,
            content: `${title ? title + " | " + process.env.NEXT_PUBLIC_WEB_TITLE : process.env.NEXT_PUBLIC_WEB_TITLE}`
        },
        {
            name: `twitter:description`,
            content: router.pathname === "/" ? process.env.NEXT_PUBLIC_DESCRIPTION : desc
        },
    ];

    const generateBreadcrumbsList = (bca: string | undefined): any[] => {
        const bcList: any[] = [];

        bcList.push({
            "@type": "ListItem",
            "position": 1,
            "name": "Doctor Car Parts",
            "item": "https://doctorcarparts.com"
        });

        if (bca === "contact") {
            bcList.push({
                "@type": "ListItem",
                "position": 2,
                "name": "Contact Us",
                "item": "https://doctorcarparts.com/contact-us"
            });
        }

        if (bca === "search") {
            bcList.push({
                "@type": "ListItem",
                "position": 2,
                "name": "Search Parts",
                "item": "https://doctorcarparts.com/search"
            });
        }

        if (bca === "about") {
            bcList.push({
                "@type": "ListItem",
                "position": 2,
                "name": "About Us",
                "item": "https://doctorcarparts.com/about"
            });
        }

        if (bca === "terms") {
            bcList.push({
                "@type": "ListItem",
                "position": 2,
                "name": "Terms & Conditions",
                "item": "https://doctorcarparts.com/terms"
            });
        }

        if (bca === "returns") {
            bcList.push({
                "@type": "ListItem",
                "position": 2,
                "name": "Returns Policy",
                "item": "https://doctorcarparts.com/returns"
            });
        }

        if (bca === "shipping") {
            bcList.push({
                "@type": "ListItem",
                "position": 2,
                "name": "Shipping Policy",
                "item": "https://doctorcarparts.com/shipping"
            });
        }

        if (bca === "carparts" || bca === "make" || bca === "model") {
            bcList.push({
                "@type": "ListItem",
                "position": 2,
                "name": "Car Parts",
                "item": "https://doctorcarparts.com/car-parts"
            });
        }

        if (bca === "make" || bca === "model" && vhLinks) {
            bcList.push({
                "@type": "ListItem",
                "position": 3,
                "name": `ALL ${make?.toUpperCase()} MODELS`,
                "item": `https://doctorcarparts.com/car-parts/${vhLinks?.make}`
            });
        }

        if (bca === "model" && vhLinks) {
            bcList.push({
                "@type": "ListItem",
                "position": 4,
                "name": `${make?.toUpperCase()} ${model?.toUpperCase()} MODELS`,
                "item": `https://doctorcarparts.com/car-parts/${vhLinks?.make}/${vhLinks?.model}`
            });
        }

        if (bca === "prime") {
            bcList.push({
                "@type": "ListItem",
                "position": 2,
                "name": `${prod?.title}`,
                "item": `https://doctorcarparts.com/products/${prod?.ref_sku}`
            });
        }

        if (bca === "parts" || bca === "genart" || bca === "prod" && vhLinks) {
            bcList.push({
                "@type": "ListItem",
                "position": 2,
                "name": `Car Parts For ${vhLinks && `${make ? make?.toUpperCase() + " " + model?.toUpperCase() : ""}`}`,
                "item": `${`https://doctorcarparts.com/car-parts/${vhLinks?.make}/${vhLinks?.model}/${ktype}`}`
            });
        }

        if (bca === "genart" || bca === "prod" && vhLinks) {
            bcList.push({
                "@type": "ListItem",
                "position": 3,
                "name": `${genartname} For ${make?.toUpperCase() + " " + model?.toUpperCase()}`,
                "item": `${`https://doctorcarparts.com/car-parts/${vhLinks?.make}/${vhLinks?.model}/${ktype}/${genartname && genartname.toLowerCase().replace(/\s+|,\s/g, "-").replace(/\\|\//g, "")}/${genartnr}`}?qt=std`
            });
        }

        if (bca === "prod" && vhLinks) {
            bcList.push({
                "@type": "ListItem",
                "position": 4,
                "name": `${prod?.title}`,
                "item": `${`https://doctorcarparts.com/car-parts/${vhLinks?.make}/${vhLinks?.model}/${ktype}/${genartname && genartname.toLowerCase().replace(/\s+|,\s/g, "-").replace(/\\|\//g, "")}/${genartnr}/${prod?.ref_sku}`}`
            });
        }

        return bcList;
    };

    const addMetaTags = (tag: {name: string; property?: string; content: string | undefined;} | {property: string; content: string | undefined; name?: string;}) => {

        if (tag.name) {
            return <meta key={tag.name} name={tag.name} content={tag.content} />;
        }

        if (tag.property) {
            return <meta key={tag.property} property={tag.property} content={tag.content} />;
        }
    };

    return (
        <Head>
            <title>{title ? title + " | " + process.env.NEXT_PUBLIC_WEB_TITLE : process.env.NEXT_PUBLIC_WEB_TITLE}</title>
            <link rel="icon" type="image/x-icon" href={`${process.env.NEXT_PUBLIC_CDN_URI}/static/icons/favicon.ico`} />
            {/* <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Marck+Script|Kalam:wght@300;400;700|Paytone+One|Kaushan+Script|Abel|Roboto|Roboto+Condensed|Noto+Sans|Open+Sans|Oswald|Oxygen|Ubuntu|Ubuntu+Condensed|Fontdiner+Swanky|Freckle+Face&display=swap"/> */}
            {prod && <link rel="canonical" href={`https://doctorcarparts.com/products/${prod?.ref_sku}`}/>}
            {rs && prod && <script type="application/ld+json">
                {`{
                    "@context": "https://schema.org/",
                    "@type": "Product",
                    "name": "${prod.title}",
                    "image": "${prod.image && prod.image.length > 0 ? prod.image : "https://images.esellerpro.com/2873/I/20/noimage.jpg"}",
                    "description": "${prod.title}",
                    "url": "${`https://doctorcarparts.com/products/${prod.ref_sku}`}",
                    "brand": {
                        "@type": "Brand",
                        "name": "${prod.brand}"
                    },
                    "offers": {
                        "@type": "Offer",
                        "url": "${`https://doctorcarparts.com/products/${prod.ref_sku}`}",
                        "priceCurrency": "GBP",
                        "price": "${price}",
                        "availability": "${stock}",
                        "seller": {
                            "@type": "Organization",
                            "name": "Doctor Car Parts"
                        }
                    }
                }`}
            </script>}
            <script type="application/ld+json">
                {`{
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": ${JSON.stringify(generateBreadcrumbsList(bc), null, 4)}
                }`}
            </script>
            {meta.map(tag => addMetaTags(tag))}
            {bc === "noindex" && <meta name="robots" content="noindex" />}
            {/* {orderId && customerEmail && countryCode && <GCR id={orderId} email={customerEmail} cc={countryCode} />} */}

            {cookies?.ga && <script async src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID}`} />}
            {cookies?.ga && <script
                dangerouslySetInnerHTML = {{
                    __html: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag() {
                            dataLayer.push(arguments);
                        }
                        gtag("js", new Date());
                        gtag("config", "${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID}", {
                            page_path: window.location.pathname
                        }); `
                }}
            />}
            {cookies?.ga && <script src="https://apis.google.com/js/platform.js" async defer />}
        </Head>
    );
};

export default SiteHead;
